import React, { useState } from 'react';
import { getTravelApiBaseUrl } from '../lib/apiConfig';
import { sessionToken, setSessionToken } from './p3Session';
import { guestTravelerId, bindStateToAccount } from './P3TestBench';

const SUPPORT_EMAIL = 'info@freshroad.com';
// Mirrors ACCOUNT_SMS_TEXT in backend/travel/p3Accounts.js (stored with each sign-up as proof of consent).
export const SMS_CONSENT = 'I agree to receive text messages from Freshroad Travel Pass, including login codes and trip notifications. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.';

const css = `
.su{ min-height:100vh; display:flex; flex-direction:column; background:#f7f7f8; color:#111827; font-family:Inter,system-ui,-apple-system,"Segoe UI",Roboto,sans-serif; }
.su *{ box-sizing:border-box; }
.su-top{ padding:20px 28px; }
.su-logo{ display:inline-flex; align-items:center; gap:10px; font-weight:800; font-size:18px; letter-spacing:-.01em; color:#111827; text-decoration:none; }
.su-mark{ width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#15263d,#2d4a73); color:#f2c46d; display:grid; place-items:center; font-size:16px; }
.su-main{ flex:1; display:flex; justify-content:center; align-items:flex-start; padding:24px 16px 48px; }
.su-card{ width:100%; max-width:460px; background:#fff; border:1px solid #e5e7eb; border-radius:16px; padding:40px 40px 32px; box-shadow:0 1px 2px rgba(16,24,40,.04),0 12px 32px rgba(16,24,40,.06); }
.su h1{ font-size:28px; line-height:1.2; font-weight:700; letter-spacing:-.02em; margin:0 0 6px; color:#111827; text-transform:none; }
.su-sub{ margin:0 0 28px; color:#6b7280; font-size:15px; }
.su-field{ margin-bottom:16px; }
.su-field label{ display:block; font-size:14px; font-weight:600; color:#374151; margin-bottom:6px; }
.su-field input{ width:100%; height:46px; font:inherit; font-size:16px; color:#111827; padding:0 14px; border:1px solid #d1d5db; border-radius:10px; background:#fff; transition:border-color .15s, box-shadow .15s; }
.su-field input:focus{ outline:none; border-color:#15263d; box-shadow:0 0 0 3px rgba(21,38,61,.15); }
.su-hint{ margin-top:6px; font-size:13px; color:#6b7280; }
.su-pw{ position:relative; }
.su-pw input{ padding-right:64px; }
.su-pw button{ position:absolute; right:6px; top:50%; transform:translateY(-50%); border:0; background:none; font:inherit; font-size:13px; font-weight:600; color:#15263d; padding:8px; cursor:pointer; text-transform:none; }
.su-check{ display:flex; gap:10px; align-items:flex-start; margin:14px 0 0; font-size:13px; line-height:1.5; color:#4b5563; cursor:pointer; }
.su-check input{ width:18px; height:18px; margin:1px 0 0; flex:0 0 18px; accent-color:#15263d; }
.su a{ color:#15263d; font-weight:600; }
.su-btn{ margin-top:24px; width:100%; height:48px; border:0; border-radius:10px; background:#15263d; color:#fff; font:inherit; font-size:16px; font-weight:600; cursor:pointer; transition:background .15s; text-transform:none; }
.su a.su-btn{ display:grid; place-items:center; color:#fff; text-decoration:none; }
.su-btn:hover:not(:disabled){ background:#1f3656; }
.su-btn:disabled{ opacity:.45; cursor:not-allowed; }
.su-err{ margin-top:14px; padding:10px 12px; border-radius:10px; background:#fef2f2; color:#b91c1c; font-size:14px; }
.su-ok{ width:56px; height:56px; border-radius:50%; background:#ecfdf5; color:#059669; display:grid; place-items:center; font-size:28px; margin-bottom:18px; }
.su-done p{ color:#4b5563; font-size:15px; line-height:1.6; }
.su-aside{ margin:-6px 0 0; text-align:right; font-size:14px; }
.su-code{ letter-spacing:.4em; font-size:22px !important; text-align:center; }
.su-note{ margin-top:14px; padding:10px 12px; border-radius:10px; background:#ecfdf5; color:#065f46; font-size:14px; }
.su-link{ border:0; background:none; padding:0; font:inherit; font-weight:600; color:#15263d; cursor:pointer; text-decoration:underline; text-transform:none; }
.su-switch{ margin:22px 0 0; text-align:center; font-size:14px; color:#6b7280; }
.su-foot{ display:flex; flex-wrap:wrap; gap:8px 20px; justify-content:center; padding:20px 16px 28px; font-size:13px; color:#6b7280; }
.su-foot a{ color:#6b7280; font-weight:400; text-decoration:none; }
.su-foot a:hover{ text-decoration:underline; }
@media (max-width:520px){ .su-card{ padding:28px 20px 24px; border-radius:14px; } .su h1{ font-size:24px; } .su-top{ padding:16px; } }
`;

const api = (path) => `${getTravelApiBaseUrl()}/api/p3/account/${path}`;

async function post(path, body, token = sessionToken()) {
  const res = await fetch(api(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.success) throw new Error(data?.message || 'Something went wrong. Please try again.');
  return data;
}

/** Signed in: keep the session, tie this browser's saved state to the account, and go on to the app or to email verification. */
function signedIn({ token, account }) {
  setSessionToken(token);
  bindStateToAccount(account.accountId);
  window.location.hash = account.emailVerified ? '#/p3' : '#/p3-verify';
}

const resetToken = () => new URLSearchParams(window.location.hash.split('?')[1] || '').get('token') || '';

const TITLES = {
  signup: ['Create your account', 'It only takes a minute.'],
  login: ['Log in', 'Welcome back.'],
  verify: ['Check your email', 'Enter the 6-digit code we sent you. It expires in 30 minutes.'],
  forgot: ['Reset your password', "Enter your account's email and we'll send you a link to set a new password."],
  reset: ['Choose a new password', 'At least 8 characters. You will be signed out on every device.'],
};

/**
 * Freshroad Travel Pass accounts: sign up (#/p3-signup, with the optional A2P texts opt-in), log in (#/p3-login,
 * with the emailed code when two-step sign-in is on), verify the email (#/p3-verify), forgot password (#/p3-forgot)
 * and the reset link from the email (#/p3-reset?token=). The app (#/p3) needs a signed-in, verified account.
 */
export default function P3Signup({ mode = 'signup' }) {
  const [form, setForm] = useState({ email: '', password: '', code: '', terms: false });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [challenge, setChallenge] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const run = (fn) => async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    setNote('');
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = run(async () => {
    if (mode === 'signup') {
      signedIn(await post('register', { ...form, code: undefined, pageUrl: window.location.href, guestId: guestTravelerId() }, ''));
    } else if (mode === 'login' && challenge) {
      signedIn(await post('login/code', { challenge, code: form.code, guestId: guestTravelerId() }, ''));
    } else if (mode === 'login') {
      const data = await post('login', { email: form.email, password: form.password, guestId: guestTravelerId() }, '');
      if (data.mfaRequired) {
        setChallenge(data.challenge);
        setNote(`We emailed a login code to ${form.email.trim()}.`);
      } else signedIn(data);
    } else if (mode === 'verify') {
      await post('verify-email', { code: form.code });
      window.location.hash = '#/p3';
    } else if (mode === 'forgot') {
      setNote((await post('forgot-password', { email: form.email }, '')).message);
      setDone(true);
    } else if (mode === 'reset') {
      await post('reset-password', { token: resetToken(), password: form.password }, '');
      setSessionToken('');
      setDone(true);
    }
  });
  const resend = run(async () => {
    await post('resend-verification', {});
    setNote('We sent a new code.');
  });
  const logOut = run(async () => {
    await post('logout', {}).catch(() => {});
    setSessionToken('');
    window.location.hash = '#/p3-login';
  });

  const codeOk = /^\d{6}$/.test(form.code.trim());
  const ready = {
    signup: form.email.trim() && form.password.length >= 8 && form.terms,
    login: challenge ? codeOk : form.email.trim() && form.password,
    verify: codeOk,
    forgot: form.email.trim(),
    reset: form.password.length >= 8 && resetToken(),
  }[mode];
  const [title, sub] = mode === 'login' && challenge ? ['Enter your login code', 'Check your email for a 6-digit code. It expires in 30 minutes.'] : TITLES[mode];
  const button = { signup: 'Create account', login: challenge ? 'Log in' : 'Log in', verify: 'Verify email', forgot: 'Send reset link', reset: 'Set new password' }[mode];

  const emailField = (
    <div className="su-field">
      <label htmlFor="su-email">Email</label>
      <input id="su-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set('email')} required dir="ltr" />
    </div>
  );
  const passwordField = (autoComplete, label = 'Password') => (
    <div className="su-field">
      <label htmlFor="su-pw">{label}</label>
      <div className="su-pw">
        <input id="su-pw" type={showPw ? 'text' : 'password'} autoComplete={autoComplete} value={form.password} onChange={set('password')} required minLength={autoComplete === 'new-password' ? 8 : undefined} aria-describedby={autoComplete === 'new-password' ? 'su-pw-hint' : undefined} />
        <button type="button" onClick={() => setShowPw((v) => !v)} aria-pressed={showPw}>{showPw ? 'Hide' : 'Show'}</button>
      </div>
      {autoComplete === 'new-password' && <div className="su-hint" id="su-pw-hint">At least 8 characters.</div>}
    </div>
  );
  const codeField = (
    <div className="su-field">
      <label htmlFor="su-code">6-digit code</label>
      <input id="su-code" className="su-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.code} onChange={set('code')} required dir="ltr" />
    </div>
  );

  return (
    <div className="su">
      <style>{css}</style>
      <header className="su-top">
        <a className="su-logo" href="#/p3"><span className="su-mark" aria-hidden="true">✦</span>Freshroad Travel Pass</a>
      </header>

      <main className="su-main">
        <div className="su-card">
          {done && mode === 'reset' ? (
            <div className="su-done">
              <div className="su-ok" aria-hidden="true">✓</div>
              <h1>Password changed</h1>
              <p>Log in with your new password.</p>
              <a className="su-btn" href="#/p3-login">Log in</a>
            </div>
          ) : done && mode === 'forgot' ? (
            <div className="su-done">
              <div className="su-ok" aria-hidden="true">✓</div>
              <h1>Check your email</h1>
              <p>{note} The link expires in 1 hour.</p>
              <a className="su-btn" href="#/p3-login">Back to log in</a>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <h1>{title}</h1>
              <p className="su-sub">{sub}</p>

              {mode === 'signup' && (
                <>
                  {emailField}
                  {passwordField('new-password')}
                  <label className="su-check">
                    <input type="checkbox" checked={form.terms} onChange={set('terms')} required />
                    <span>I agree to the <a href="#/site/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="#/site/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
                  </label>
                </>
              )}
              {mode === 'login' && !challenge && (
                <>
                  {emailField}
                  {passwordField('current-password')}
                  <p className="su-aside"><a href="#/p3-forgot">Forgot password?</a></p>
                </>
              )}
              {((mode === 'login' && challenge) || mode === 'verify') && codeField}
              {mode === 'forgot' && emailField}
              {mode === 'reset' && (resetToken() ? passwordField('new-password', 'New password') : <div className="su-err" role="alert">This link is missing its code. Open the link from the email again, or ask for a new one.</div>)}

              <button className="su-btn" type="submit" disabled={busy || !ready}>{busy ? 'Please wait…' : button}</button>
              {note && <div className="su-note" role="status">{note}</div>}
              {error && <div className="su-err" role="alert">{error}</div>}

              {mode === 'signup' && <p className="su-switch">Already have an account? <a href="#/p3-login">Log in</a></p>}
              {mode === 'login' && <p className="su-switch">New here? <a href="#/p3-signup">Create an account</a></p>}
              {mode === 'verify' && (
                <p className="su-switch">
                  No email? <button type="button" className="su-link" onClick={resend} disabled={busy}>Send a new code</button> · <button type="button" className="su-link" onClick={logOut} disabled={busy}>Use another account</button>
                </p>
              )}
              {(mode === 'forgot' || mode === 'reset') && <p className="su-switch"><a href="#/p3-login">Back to log in</a></p>}
            </form>
          )}
        </div>
      </main>

      <footer className="su-foot">
        <span>© {new Date().getFullYear()} Fresh Road Inc.</span>
        <a href="#/site/terms">Terms</a>
        <a href="#/site/privacy">Privacy</a>
        <a href={`mailto:${SUPPORT_EMAIL}`}>Help</a>
      </footer>
    </div>
  );
}
